import { FC, useRef, useState, useMemo } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import Filter from 'dibs-icons/exports/Filter';
import * as tracking from 'dibs-tracking';
import classNames from 'classnames';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { SbRespRefineTopNav_itemSearch$data } from './__generated__/SbRespRefineTopNav_itemSearch.graphql';
import { SbRespRefineTopNav_user$data } from './__generated__/SbRespRefineTopNav_user.graphql';
import { SbRespRefineTopNav_viewer$data } from './__generated__/SbRespRefineTopNav_viewer.graphql';
import { SbRespRefineTopFilters } from './SbRespRefineTopFilters';
import { SbRespRefineMenuDrawer } from '../SbRespRefineMenuDrawer/SbRespRefineMenuDrawer';
import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { trackSearchWithinResults } from '../../utils/tracking/searchBrowse/filterTracking';
import { SbSharedRefineMenuSearchWithin } from '../SbSharedRefineMenu/SbSharedRefineMenuSearchWithin/SbSharedRefineMenuSearchWithin';
import { SbRespRefineTopAppliedFilters } from './SbRespRefineTopAppliedFilters';
import { sortOrderLabel } from '../../shared/sharedMessages';

import styles from './SbRespRefineTopNav.scss';

type Props = {
    itemSearch: SbRespRefineTopNav_itemSearch$data;
    viewer: SbRespRefineTopNav_viewer$data;
    user: SbRespRefineTopNav_user$data;
    currency: string;
    isClient: boolean;
};

const FILTER_BUTTON_TOP_MARGIN = 128;

const SbRespRefineTopNavComponent: FC<Props> = ({
    itemSearch,
    user,
    viewer,
    isClient,
    currency,
}) => {
    const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
    const [isFiltersButtonSticking, setIsFiltersButtonSticking] = useState(false);
    const topSectionElementRef = useRef<HTMLDivElement>(null);

    const rightMargin = useMemo(() => {
        if (!isClient) {
            return 0;
        }
        const mainContentElement = document.querySelector('#mainContent');
        if (mainContentElement instanceof HTMLElement) {
            return mainContentElement.offsetLeft;
        }
        return 0;
    }, [isClient]);

    return (
        <>
            <div className={styles.header}>
                <FormattedMessage
                    id="SbRespRefineTopNav.filterResults"
                    defaultMessage="Filter Results"
                />
            </div>
            <div ref={topSectionElementRef} className={styles.topSection}>
                <VisibilityTracker
                    elementRef={topSectionElementRef}
                    onVisibilityChange={({ intersectionRatio }: { intersectionRatio: number }) =>
                        setIsFiltersButtonSticking(intersectionRatio < 1)
                    }
                    observerOptions={{
                        //When 1 pixel goes off screen then start floating, so that there would be no visible jump
                        rootMargin: `-${FILTER_BUTTON_TOP_MARGIN + 1}px 0px 0px 0px`,
                        threshold: [1],
                    }}
                    watchAfterFirstVisible
                />
                <SbRespRefineTopFilters
                    itemSearch={itemSearch}
                    viewer={viewer}
                    user={user}
                    currency={currency}
                    isClient={isClient}
                />
                <div
                    style={
                        isFiltersButtonSticking
                            ? { right: rightMargin, top: FILTER_BUTTON_TOP_MARGIN }
                            : {}
                    }
                    className={classNames(styles.allFiltersButton, {
                        [styles.allFiltersButtonSticking]: isFiltersButtonSticking,
                    })}
                    onClick={() => {
                        setIsFiltersDrawerOpen(true);
                        tracking.trackEvent({
                            category: 'results page refinement',
                            action: 'all filters opened',
                        });
                    }}
                >
                    <Filter className={styles.filterIcon} />
                    <FormattedMessage
                        id="SbRespRefineTopNav.allFiltersCta"
                        defaultMessage="All Filters"
                    />
                </div>
            </div>
            <div className={styles.middleSection}>
                <SbSharedRefineMenuSearchWithin
                    // @ts-ignore until SbSharedRefineMenuSearchWithin is converted
                    horizontalSpacing="small"
                    onSubmit={trackSearchWithinResults}
                    itemSearch={itemSearch}
                />
                <div className={styles.sortAndPriceContainer}>
                    <span className={styles.label}>
                        <FormattedMessage
                            id="SbRespRefineTopNav.pageSizeLabel"
                            defaultMessage="View"
                        />
                    </span>
                    <SbRespPageSize itemSearch={itemSearch} usePageSizeLinks />
                    <span className={styles.label}>{sortOrderLabel}</span>
                    <SbRespSortItems itemSearch={itemSearch} />
                </div>
            </div>
            <div className={styles.bottomSection}>
                <SbRespRefineTopAppliedFilters
                    currency={currency}
                    itemSearch={itemSearch}
                    user={user}
                />
            </div>
            <SbRespRefineMenuDrawer
                itemSearch={itemSearch}
                viewer={viewer}
                user={user}
                currency={currency}
                isClient={isClient}
                isOpen={isFiltersDrawerOpen}
                closeDrawer={() => setIsFiltersDrawerOpen(false)}
            />
        </>
    );
};

export const SbRespRefineTopNav = createFragmentContainer(SbRespRefineTopNavComponent, {
    itemSearch: graphql`
        fragment SbRespRefineTopNav_itemSearch on ItemSearchQueryConnection {
            ...SbRespRefineTopAppliedFilters_itemSearch
            ...SbRespSortItems_itemSearch
            ...SbRespPageSize_itemSearch
            ...SbRespRefineTopFilters_itemSearch
            ...SbSharedRefineMenuSearchWithin_itemSearch
            ...SbRespRefineMenuDrawer_itemSearch
        }
    `,
    user: graphql`
        fragment SbRespRefineTopNav_user on User {
            ...SbRespRefineTopAppliedFilters_user
            ...SbRespRefineTopFilters_user
            ...SbRespRefineMenuDrawer_user
        }
    `,
    viewer: graphql`
        fragment SbRespRefineTopNav_viewer on Viewer {
            ...SbRespRefineTopFilters_viewer
            ...SbRespRefineMenuDrawer_viewer
        }
    `,
});
